import React, { useEffect, useState } from 'react';
import { Typography, Button } from '@mui/material';
import { AiOutlineClockCircle } from 'react-icons/ai';
import { BiDish } from 'react-icons/bi';
import { GiForkKnifeSpoon } from 'react-icons/gi';
import { AiOutlineClose, AiFillDelete } from 'react-icons/ai';
import { RiBookmarkFill, RiBookmarkLine } from 'react-icons/ri';
import { GrEdit } from 'react-icons/gr';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { useSelector, useDispatch } from 'react-redux';
import Modal from 'styled-react-modal';
import clsx from 'clsx';
import IngredientBox from '../Ingredient Box';
import DirectionBox from '../Direction Box';
import Detail from '../Detail';
import { removeRecipe, updateFavorite } from '../../redux/actions/recipes';
import { resetPickedRecipe } from '../../redux/actions/pickedRecipe';
import { useStyles } from './styles';
import './styles.sass';

// firebase
import { doc, updateDoc, deleteDoc } from 'firebase/firestore/lite';
import { db } from '../../services';
import EditingDialog from '../Editing Dialog';

const Recipe = ({ setShowRecipe }) => {
    const recipe = useSelector(state => state.pickedRecipe);
    const recipes = useSelector(state => state.recipes);
    const [favorite, setFavorite] = useState(false);
    const [openAlertModal, setOpenAlertModal] = useState(false);
    const [openEditingModal, setOpenEditingModal] = useState(false);
    const classes = useStyles();
    const dispatch = useDispatch();

    const onDeleteRecipe = async () => {
        const index = recipes.findIndex(element => element.id === recipe.id);
        try {
            await deleteDoc(doc(db, "recipes", recipe.id));
        }
        catch (error) {
            console.log(error.message);
        }
        finally {
            setShowRecipe(false);
            dispatch(removeRecipe(index)); // Update store
            dispatch(resetPickedRecipe()); // Reset picked recipe to initial state
            // Check if there's an image at cloudinary, if so - delete it
            if (recipe.image.public_id)
                fetch(`https://cloudinary-management.herokuapp.com/delete-image?public_id=${recipe.image.public_id}`)
                    .then((res) => res.json())
                    .then((res) => console.log(res))
                    .catch((error) => console.log("error: ", error.message));
        }
    }

    const handleCloseAlertModal = async () => {
        setOpenAlertModal(false);
    }

    const handleClose = () => {
        setShowRecipe(false);
        dispatch(resetPickedRecipe());
    }

    const handleFavoriteChange = async (id, favorite) => {
        const recipeRef = doc(db, "recipes", id);
        try {
            // Update document on Firestore
            await updateDoc(recipeRef, { favorite: favorite });
        }
        catch (error) {
            console.log(error.message);
        }
        finally {
            setFavorite(favorite);
            const index = recipes.findIndex(recipes => recipes.id === id);
            dispatch(updateFavorite(index, favorite)); // Update store
        }
    }

    useEffect(() => {
        setFavorite(recipe.favorite);
    }, [recipe.favorite]);

    return (
        <>
            <div className="recipe-container">
                <div className="header">
                    <div className="wrapper">
                        <img
                            src={recipe.image.url}
                            alt={recipe.name}
                            className="recipe-image"
                        />
                        <Button
                            onClick={handleClose}
                            className={clsx(classes.button, classes.close)}
                            disableRipple
                        >
                            <AiOutlineClose style={{ fontSize: 20 }} />
                        </Button>
                        <div className="recipe-actions">
                            <Button
                                onClick={() => handleFavoriteChange(recipe.id, !favorite)}
                                className={classes.button}
                                disableRipple
                            >
                                {favorite ?
                                    <RiBookmarkFill style={{ fontSize: 20 }} />
                                    :
                                    <RiBookmarkLine style={{ fontSize: 20 }} />
                                }
                            </Button>
                            <Button
                                onClick={() => setOpenEditingModal(true)}
                                className={classes.button}
                                disableRipple
                            >
                                <GrEdit className="icon" />
                            </Button>
                            <Button
                                onClick={() => setOpenAlertModal(true)}
                                className={classes.button}
                                disableRipple
                            >
                                <AiFillDelete style={{ fontSize: 22 }} />
                            </Button>
                        </div>
                    </div>
                    <Typography variant='h4' className={classes.name}>{recipe.name}</Typography>
                </div>
                <div className="recipe-details">
                    <div className="detail">
                        <AiOutlineClockCircle className="icon" />
                        <div className="info">
                            <Typography variant='h6' className={classes.text}>זמן הכנה</Typography>
                            <Typography variant='subtitle1' className={classes.text}>
                                {`${recipe.time.value} ${recipe.time.unit}`}
                            </Typography>
                        </div>
                    </div>
                    <div className="detail middle">
                        <BiDish className="icon" />
                        <div className="info">
                            <Typography variant='h6' className={classes.text}>כמות</Typography>
                            <Typography variant='subtitle1' className={classes.text}>
                                {Number(recipe.quantity) === 1 ? 'מנה אחת' : `${recipe.quantity} מנות`}
                            </Typography>
                        </div>
                    </div>
                    <div className="detail">
                        <GiForkKnifeSpoon className="icon" />
                        <div className="info">
                            <Typography variant='h6' className={classes.text}>קטגוריה</Typography>
                            <Typography variant='subtitle1' className={classes.text}>
                                {recipe.category}
                            </Typography>
                        </div>
                    </div>
                </div>
                <div className="mobile-recipe-details">
                    <Detail
                        type={'time'}
                        value={`${recipe.time.value} ${recipe.time.unit}`}
                    />
                    <div className="seperator" />
                    <Detail
                        type={'quantity'}
                        value={Number(recipe.quantity) === 1 ? 'מנה אחת' : `${recipe.quantity} מנות`}
                    />
                    <div className="seperator" />
                    <Detail
                        type={'category'}
                        value={recipe.category}
                    />
                </div>
                <div className="recipe-ingredients-and-directions">
                    <div className="ingredients-container">
                        <Typography variant='h5' className={clsx(classes.text, classes.title)}>מרכיבים</Typography>
                        <div className="ingredients">
                            {recipe.ingredients.map((ingredient, index) => {
                                return (
                                    <IngredientBox
                                        key={index}
                                        index={index}
                                        title={ingredient.title}
                                        amount={ingredient.amount}
                                        unit={ingredient.unit}
                                    />
                                );
                            })}
                        </div>
                    </div>
                    <div className="directions-container">
                        <Typography variant='h5' className={clsx(classes.text, classes.title)}>אופן ההכנה</Typography>
                        <div className="directions">
                            {recipe.directions.map((direction, index) => {
                                return (
                                    <DirectionBox
                                        key={index}
                                        index={index}
                                        value={direction}
                                        size={recipe.directions.length}
                                    />
                                )
                            })}
                        </div>
                    </div>
                </div>
            </div>
            <EditingDialog
                openEditingModal={openEditingModal}
                setOpenEditingModal={setOpenEditingModal}
            />
            <AlertModal
                isOpen={openAlertModal}
                onBackgroundClick={handleCloseAlertModal}
                onEscapeKeydown={handleCloseAlertModal}
            >
                <div className="modal-header">
                    <WarningAmberRoundedIcon className="icon" />
                    <Typography variant='h6' className={classes.text}>למחוק את {recipe.name}?</Typography>
                </div>
                <Typography variant='subtitle1' className={classes.text}>פעולה זו תמחק את המתכון לצמיתות ולא תוכל לשחזר אותו.</Typography>
                <div className="modal-buttons">
                    <Button
                        className={clsx(classes.modalButton, classes.filled)}
                        onClick={handleCloseAlertModal}
                    >
                        לא, אל תמחק
                    </Button>
                    <Button
                        className={clsx(classes.modalButton, classes.unfilled)}
                        onClick={onDeleteRecipe}
                    >
                        כן, מחק

                    </Button>
                </div>
            </AlertModal>
        </>
    )
}

const AlertModal = Modal.styled`
    width: 500px;
    height: fit-content;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    border-radius: 15px;
    background-color: #000f14;
    padding: 20px;
    cursor: default;
    
    @media (max-width: 550px) {
        width: 100%;
    }
`;

export default Recipe;